import { pathToRegexp } from 'path-to-regexp'
import { type AnyObject } from '../types/common'

export interface Route<Path extends string = string> {
	id: string
	path: Path
	route: (params: ParamObject<Path>) => string
	regex: RegExp
	isProtected: boolean
	isLocalized: boolean
}

// Extract parameter names from the path
const extractParams = (path: string): string[] => {
	const { keys } = pathToRegexp(path)
	return keys.map((key) => key.name)
}

// Helper types to infer parameter names
type ExtractParamsFromPath<Path extends string> =
	Path extends `${string}:${infer Param}/${infer Rest}`
		? Param | ExtractParamsFromPath<Rest>
		: Path extends `${string}:${infer Param}`
			? Param
			: never

type ParamObject<Path extends string> = {
	[K in ExtractParamsFromPath<Path>]: string
}

/**
 * Create a new route in the routing system
 * @param path `string` The path. Set params here with `:param` notation.
 * @param protect `boolean`, to indicate if the route is protected. Defaults to false.
 *
 * @returns Route object.
 */
export const createRoute = <Path extends string>(
	path: Path,
	options: {
		id: string
		protect?: boolean
		localize?: boolean
	},
): Route<Path> => {
	const paramNames = extractParams(path) as ExtractParamsFromPath<Path>[]

	return {
		id: options.id,
		path,
		route: (params: ParamObject<Path>) => {
			let resultPath = path
			paramNames.forEach((paramName) => {
				if (!(paramName in params)) {
					throw new Error(`Missing parameter: ${paramName}`)
				}
				resultPath = resultPath.replace(
					`:${paramName}`,
					params[paramName],
				) as Path
			})
			return resultPath
		},
		regex: pathToRegexp(path),
		isProtected: options.protect ?? false,
		isLocalized: options.localize ?? false,
	}
}

export const renameKeys = (
	obj: AnyObject<any>,
	prefix: string,
): AnyObject<any> => {
	const keyValues = Object.keys(obj).map((key) => {
		const newKey = `${prefix}_${key}`
		return { [newKey]: obj[key] }
	})
	return Object.assign({}, ...keyValues)
}

export const arrayToObject = (arr: any[]) => {
	const finalObject = {}

	for (let i = 0; i < arr.length; i++) {
		Object.assign(finalObject, arr[i])
	}

	return finalObject
}

export function flattenObject(
	obj: Record<string, any>,
	prefix = '',
): Record<string, any> {
	return Object.keys(obj).reduce(
		(acc, key) => {
			const value = obj[key]
			const newKey = prefix ? `${prefix}.${key}` : key

			if (
				typeof value === 'object' &&
				value !== null &&
				!Array.isArray(value)
			) {
				Object.assign(acc, flattenObject(value, newKey))
			} else {
				// If the value is not an object, assign the parent object as the value
				acc[prefix] = obj
			}

			return acc
		},
		{} as Record<string, any>,
	)
}
